// src/pages/LoginPage.jsx
import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Form, Modal } from 'react-bootstrap';
import { AuthContext } from '../context/AuthContext';
import GoogleLoginButton from '../components/GoogleLoginButton';
import { toast } from 'react-toastify';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [consent, setConsent] = useState(null); // { code, mensaje, nombreUsuario, credential, nonce }
  const [linkPassword, setLinkPassword] = useState('');
  const [linking, setLinking] = useState(false);

  const { login, logout, loginWithGoogle, linkGoogle } = useContext(AuthContext);
  const navigate = useNavigate();

  const finishLogin = (userRole, google) => {
    if (userRole !== 'ROL_CLIENTE') {
      toast.error('Este acceso es exclusivo para clientes.', {
        position: 'top-right',
        autoClose: 5000,
      });
      logout();
      return;
    }
    navigate('/');
    if (google?.linked) {
      toast.info(`Vinculamos tu cuenta de Google. Tu usuario es ${google.username}.`, {
        position: 'top-right',
        autoClose: 8000,
      });
      return;
    }
    if (google?.isNewUser) {
      toast.success(`¡Cuenta creada! Tu usuario es ${google.username}.`, {
        position: 'top-right',
        autoClose: 8000,
      });
      return;
    }
    toast.success('¡Bienvenido de nuevo!', {
      position: 'top-right',
      autoClose: 3000,
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      finishLogin(await login(username, password));
    } catch (error) {
      console.error('Login error:', error);
      const { code, mensaje } = error.response?.data || {};
      // Cuenta creada con Google: siempre entra con el botón de Google.
      if (code === 'PASSWORD_NOT_SET') {
        toast.info(
          mensaje || 'Esta cuenta se creó con Google: entra con Google o crea una contraseña desde tu perfil.',
          { position: 'top-right', autoClose: 8000 }
        );
        return;
      }
      toast.error('Credenciales incorrectas. Intenta de nuevo.', {
        position: 'top-right',
        autoClose: 5000,
      });
    }
  };

  // Google no crea ni vincula cuentas sin permiso: el backend avisa y aquí se confirma.
  const handleGoogle = async (credential, nonce) => {
    try {
      const google = await loginWithGoogle(credential, nonce, { createIfMissing: false });
      finishLogin(google.role, google);
    } catch (error) {
      const { code, mensaje, nombreUsuario } = error.response?.data || {};
      if (code === 'ACCOUNT_REQUIRED' || code === 'LINK_REQUIRED') {
        setLinkPassword('');
        setConsent({ code, mensaje, nombreUsuario, credential, nonce });
        return;
      }
      toast.error(mensaje || 'No se pudo iniciar sesión con Google.', {
        position: 'top-right',
        autoClose: 6000,
      });
    }
  };

  const handleConsent = async e => {
    e.preventDefault();
    if (!consent) return;

    setLinking(true);
    try {
      const google =
        consent.code === 'ACCOUNT_REQUIRED'
          ? await loginWithGoogle(consent.credential, consent.nonce)
          : await linkGoogle({
              credential: consent.credential,
              nonce: consent.nonce,
              nombreUsuario: consent.nombreUsuario,
              clave: linkPassword,
            });
      setConsent(null);
      finishLogin(google.role, google);
    } catch (error) {
      toast.error(error.response?.data?.mensaje || 'No se pudo continuar con Google.', {
        position: 'top-right',
        autoClose: 6000,
      });
    } finally {
      setLinking(false);
    }
  };

  const cancelConsent = () => {
    const code = consent?.code;
    setConsent(null);
    setLinkPassword('');
    toast.info(
      code === 'LINK_REQUIRED'
        ? 'No vinculamos tu Google: entra con tu usuario y contraseña.'
        : 'No creamos tu cuenta. Puedes registrarte desde «Regístrate aquí».',
      { position: 'top-right', autoClose: 6000 }
    );
  };

  return (
    <div className="login-wrapper font-inter">
      <div className="login-card">
        <div className="login-card__inner">
          <div className="login-header">
            <h1 className="login-title">
              AEC<span className="text-fuchsia-electric">Block</span>
            </h1>
            <p className="login-subtitle">Inicia sesión en tu cuenta</p>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="username">Nombre de Usuario</label>
              <input
                type="text"
                id="username"
                name="username"
                autoComplete="username"
                value={username}
                onChange={e => setUsername(e.target.value)}
                placeholder="Ingresa tu nombre de usuario"
                required
              />
            </div>

            <div className="form-group position-relative">
              <label htmlFor="password">Contraseña</label>
              <div className="input-with-toggle position-relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Ingresa tu contraseña"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(prev => !prev)}
                  className="password-toggle-btn"
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? '🔒' : '🔑'}
                </button>
              </div>
            </div>

            <button type="submit" className="btn-login">
              Iniciar Sesión
            </button>

            <GoogleLoginButton onCredential={handleGoogle} />

            <div className="login-links">
              <Link to="/resetearClave" className="login-link">
                ¿Olvidaste tu contraseña?
              </Link>

              <p>
                ¿No tienes una cuenta?{' '}
                <Link to="/registro" className="register-link">
                  Regístrate aquí
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>

      <Modal show={Boolean(consent)} onHide={cancelConsent} centered>
        <Form onSubmit={handleConsent}>
          <Modal.Header closeButton>
            <Modal.Title>
              {consent?.code === 'ACCOUNT_REQUIRED'
                ? 'Crear cuenta con Google'
                : 'Vincular tu cuenta de Google'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>{consent?.mensaje}</p>
            {consent?.code === 'LINK_REQUIRED' && (
              <>
                <p className="mb-2">
                  Usuario: <strong>{consent.nombreUsuario}</strong>
                </p>
                <Form.Group controlId="claveVinculo">
                  <Form.Label>Contraseña de tu cuenta</Form.Label>
                  <Form.Control
                    type="password"
                    autoComplete="current-password"
                    value={linkPassword}
                    onChange={e => setLinkPassword(e.target.value)}
                    required
                  />
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={cancelConsent} disabled={linking}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={linking}>
              {consent?.code === 'ACCOUNT_REQUIRED' ? 'Crear cuenta' : 'Vincular y entrar'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default LoginPage;
